import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { fetchCurrentUser, fetchRoleAccess } from './store/slices/authSlice';
import theme from './theme';
import GlobalStyles from './GlobalStyles';
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RequireModule from './components/auth/RequireModule';

const LoginPage     = lazy(() => import('./pages/Login/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/Login/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/Login/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/Dashboard/DashboardPage'));
const CalendarPage  = lazy(() => import('./pages/Calendar/CalendarPage'));
const InvoicePage   = lazy(() => import('./pages/Invoice/InvoicePage'));
const ClientsPage   = lazy(() => import('./pages/Clients/ClientsPage'));
const ClientDetailsPage = lazy(() => import('./pages/Clients/ClientDetailsPage'));
const StaffPage     = lazy(() => import('./pages/Staff/StaffPage'));
const StaffDetailsPage = lazy(() => import('./pages/Staff/StaffDetailsPage'));
const MobileHomeSettingsPage = lazy(() => import('./pages/MobileHome/MobileHomeSettingsPage'));
const LeadsPage     = lazy(() => import('./pages/Leads/LeadsPage'));
const MarketingPage = lazy(() => import('./pages/Marketing/MarketingPage'));
const MembershipPage= lazy(() => import('./pages/Membership/MembershipPage'));
const PackagesPage  = lazy(() => import('./pages/Packages/PackagesPage'));
const PackageRedemptionsPage = lazy(() => import('./pages/PackageRedemptions/PackageRedemptionsPage'));
const VouchersPage  = lazy(() => import('./pages/Vouchers/VouchersPage'));
const InventoryPage = lazy(() => import('./pages/Inventory/InventoryPage'));
const ExpensesPage = lazy(() => import('./pages/Expenses/ExpensesPage'));
const ReportsPage   = lazy(() => import('./pages/Reports/ReportsPage'));
const SettingsPage      = lazy(() => import('./pages/Settings/SettingsPage'));
const BranchesPage      = lazy(() => import('./pages/Branches/BranchesPage'));
const ServicesPage      = lazy(() => import('./pages/Services/ServicesPage'));
const ServiceOverviewPage = lazy(() => import('./pages/Services/ServiceOverviewPage'));
const PayrollPage       = lazy(() => import('./pages/Payroll/PayrollPage'));
const LoyaltyPage       = lazy(() => import('./pages/Loyalty/LoyaltyPage'));
const POSPage           = lazy(() => import('./pages/POS/POSPage'));

const Loader: React.FC = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#F8F8F8' }}>
    <div style={{ width:40, height:40, border:'3px solid #EEEEEE', borderTop:'3px solid #BC9979', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Restores `user` from the still-valid token after a page refresh — the
// token itself persists in localStorage, but the profile/role only ever
// lived in Redux memory (see authSlice's fetchCurrentUser).
const SessionBootstrap: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector(s => s.auth);

  useEffect(() => {
    if (token && !user) dispatch(fetchCurrentUser());
  }, [token, user, dispatch]);

  useEffect(() => {
    if (user?.role) dispatch(fetchRoleAccess(user.role));
  }, [user?.role, dispatch]);

  return null;
};

const App: React.FC = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <SessionBootstrap />
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pos" element={<ProtectedRoute><RequireModule module="pos"><POSPage /></RequireModule></ProtectedRoute>} />

            {/* Protected — all routes inside MainLayout require a token */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index               element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard"    element={<DashboardPage />} />
              <Route path="calendar"     element={<RequireModule module="appointments"><CalendarPage /></RequireModule>} />
              <Route path="invoices"     element={<RequireModule module="invoices"><InvoicePage /></RequireModule>} />
              <Route path="clients"      element={<RequireModule module="clients"><ClientsPage /></RequireModule>} />
              <Route path="clients/:id"  element={<RequireModule module="clients"><ClientDetailsPage /></RequireModule>} />
              <Route path="staff"        element={<RequireModule module="staff"><StaffPage /></RequireModule>} />
              <Route path="staff/:id"    element={<RequireModule module="staff"><StaffDetailsPage /></RequireModule>} />
              <Route path="leads"        element={<RequireModule module="leads"><LeadsPage /></RequireModule>} />
              <Route path="marketing"    element={<RequireModule module="marketing"><MarketingPage /></RequireModule>} />
              <Route path="coupons"      element={<Navigate to="/vouchers" replace />} />
              <Route path="vouchers"     element={<RequireModule module="membership"><VouchersPage /></RequireModule>} />
              <Route path="membership"   element={<RequireModule module="membership"><MembershipPage /></RequireModule>} />
              <Route path="packages"     element={<RequireModule module="membership"><PackagesPage /></RequireModule>} />
              <Route path="package-redemptions" element={<RequireModule module="membership"><PackageRedemptionsPage /></RequireModule>} />
              <Route path="loyalty"      element={<RequireModule module="marketing"><LoyaltyPage /></RequireModule>} />
              <Route path="inventory"    element={<RequireModule module="inventory"><InventoryPage /></RequireModule>} />
              <Route path="expenses"     element={<RequireModule module="settings"><ExpensesPage /></RequireModule>} />
              <Route path="reports"      element={<RequireModule module="reports"><ReportsPage /></RequireModule>} />
              <Route path="settings"       element={<RequireModule module="settings"><SettingsPage /></RequireModule>} />
              <Route path="mobile-home"    element={<RequireModule module="settings"><MobileHomeSettingsPage /></RequireModule>} />
              <Route path="branches"      element={<RequireModule module="settings"><BranchesPage /></RequireModule>} />
              <Route path="services"      element={<RequireModule module="settings"><ServicesPage /></RequireModule>} />
              <Route path="services/:id"  element={<RequireModule module="settings"><ServiceOverviewPage /></RequireModule>} />
              <Route path="payroll"       element={<RequireModule module="staff"><PayrollPage /></RequireModule>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  </Provider>
);

export default App;
